import { Component, h, Prop, Host } from '@stencil/core';
import { ResponsiveImages, Teaser } from '../../helpers/utils/interfaces';
import {
  getTeaserText,
  getTenant,
  renderResponsiveImages,
  renderResponsiveYoutubeImages,
  renderYoutubeImages,
} from '../../helpers/helpers';
import { getSvg } from '../../assets/svg/svg';
import { renderTools } from '../../helpers/RenderTools';

@Component({
  tag: 'cms-teaser',
  styleUrl: 'cms-teaser.scss',
  shadow: true,
})
export class CmsTeaser {
  private readonly accessibleNamePlaceholder = '%VALUE%';

  /**
   * The teaser to render @see Teaser
   */
  @Prop() teaser: Teaser;

  /**
   * ResponsiveImage sizes
   */
  @Prop() imageSizes: string;

  /**
   * Label for more-link
   */
  @Prop() moreInfoLabel: string;

  /**
   * Label for PDF-Download-Link
   */
  @Prop() downloadPdfLabel: string;

  /**
   * Always show full text in teaser
   */
  @Prop() ignoreTrimText = false;

  /**
   * Disable margin-top for text-wrapper
   */
  @Prop() reducedMargins = false;

  /**
   * Set the accessible-name for teasers (ignoring the accessible name provided by the teaser itself)
   */
  @Prop() teaserAccessibleName?: string;

  private renderMediaController() {
    if (this.teaser.youtubeThumbnail) {
      return renderYoutubeImages(this.teaser, this.imageSizes);
    }
    if (this.teaser.youtubeId) {
      const youTubeImages = renderTools.generateYoutubeImages(
        this.teaser.youtubeId,
      );
      return renderResponsiveYoutubeImages(
        youTubeImages,
        this.imageSizes,
        this.teaser.altText,
        'yt-image',
      );
    }
    return this.renderTeaserImages();
  }

  private renderTeaserImages(): ResponsiveImages {
    if (this.teaser?.imageDerivates) {
      const altText = this.teaser?.altText || '';
      const images = this.teaser?.imageDerivates;
      const imagesObj: ResponsiveImages = {
        bp320: `${images['320w']}.webp`,
        bp480: `${images['480w']}.webp`,
        bp768: `${images['768w']}.webp`,
        bp992: `${images['992w']}.webp`,
        bp1200: `${images['1200w']}.webp`,
        jpg992Default: images['992w'],
      };
      return renderResponsiveImages(
        imagesObj,
        this.imageSizes,
        altText,
        'teaser-image',
      );
    }
    return null;
  }

  private isPdfFile(url: string): boolean {
    return url && url.toLowerCase().endsWith('.pdf');
  }

  private parseTeaserAccessibleName(accessibleName: string, headline: string) {
    return accessibleName.replace(this.accessibleNamePlaceholder, headline);
  }

  render() {
    const { headline, detail, url, badge, frameTarget, uid } = this.teaser;
    const accessibleName = this.teaserAccessibleName
      ? this.parseTeaserAccessibleName(this.teaserAccessibleName, headline)
      : this.teaser.accessibleName;

    return (
      <Host class='teaser-container grid-item'>
        <a
          aria-label={accessibleName}
          href={url}
          target={frameTarget ?? '_self'}
          class='teaser-link tracking-element'
          title={headline}
          data-event='teaser_click'
          data-uid={uid ?? null}
        >
          <div style={{ position: 'relative' }} class='teaser-media-wrapper'>
            <div class='badge-wrapper'>
              {badge != null && badge !== '' ? (
                <cms-badge name={badge} theme='service' size='badge-sm' />
              ) : null}
            </div>
            {this.renderMediaController()}
          </div>
          <div class='teaser-content-wrapper'>
            {getTenant() === 'hornbach' ? (
              <div innerHTML={getSvg('cardScratch')} class='scratch' />
            ) : null}
            <cms-headline
              style={{
                '--cms-spacing-top': '0',
                '--cms-spacing-left-right': '0',
                '--cms-theme-text-color':
                  'var(--cms-semanticColors-base-strong-text)',
                '--cms-grid-padding': '0',
              }}
              headlineText={headline}
              headlineTag='h3'
              headlineClass='teaser-headline'
              isAdaptive={true}
            />
          </div>
          <div class={`text-wrapper ${this.reducedMargins ? 'no-margin' : ''}`}>
            <div
              class='teaser-text'
              innerHTML={
                this.ignoreTrimText
                  ? detail
                  : getTeaserText(detail, this.moreInfoLabel)
              }
            />
            {this.isPdfFile(url) ? (
              <div class='download-pdf-wrapper'>
                <cms-icon
                  icon='transferDownload'
                  size='md'
                  colorToken='--cms-semanticColors-base-inverted-strong-iconOnBackground'
                />
                <div class='pdf-label'>
                  {this.downloadPdfLabel.toUpperCase()}
                </div>
              </div>
            ) : null}
          </div>
        </a>
      </Host>
    );
  }
}
